/**
 * What the molecule viewer binds to — the TYPE, never a producer.
 *
 * VISUALIZATION_PLAN §5 / contract C2: a viewer declares a typed object
 * (`Molecule`), never "the output of `affdo.fragment`". That single rule is
 * what makes the viewer reusable across flows — any flow whose graph yields a
 * molecule-shaped artifact gets it for free, and nothing about
 * `full_affdo_sequential` is baked in.
 *
 * Concretely: nothing in this module — or in anything that consumes it — may
 * read `producer_tile` or a flow/tile name. The only inputs are the artifact's
 * `typed_object` and its registered payload names. `viewer-lazy-boundary.test`
 * pins that; `molecule-structure.test` pins the behaviour.
 *
 * Each entry's format set is transcribed from the Python typed object's own
 * `Literal`, so a format that cannot exist on the wire cannot be selected here:
 *
 * | typed object       | field                          | formats                 |
 * |--------------------|--------------------------------|-------------------------|
 * | `Molecule`         | `MoleculeStructure.format`     | sdf, mol, mol2, pdb     |
 * | `ProteinStructure` | `ProteinStructureFile.format`  | pdb, mmcif              |
 * | `ConformerEnsemble`| `ConformerGeometry.format`     | sdf, xyz, mol           |
 *
 * `StructureState` is deliberately ABSENT. Its coordinates live in Amber
 * `rst7`/`inpcrd`, which no browser parser reads, and its `pdb` companion is
 * optional and hash-excluded — so a `StructureState` may legitimately carry no
 * viewable structure at all (flowstack#534 recon). Listing it would promise a
 * viewer that renders nothing for most instances.
 */

/** A structure format the viewer can parse, as 3Dmol.js names it. */
export type StructureFormat = "sdf" | "mol2" | "pdb" | "xyz" | "mmcif";

/**
 * Payload-name extension → the format string 3Dmol.js expects.
 *
 * `.mol` maps to `sdf`: an MDL molfile is the single-record body of an SDF and
 * 3Dmol.js parses it with the same reader. `.cif` and `.mmcif` both map to
 * `mmcif` — `ProteinStructure.format` spells it `mmcif` while the conventional
 * file extension is `.cif`, and only the extension reaches this function.
 */
const EXTENSION_TO_FORMAT: ReadonlyMap<string, StructureFormat> = new Map([
  ["sdf", "sdf"],
  ["mol", "sdf"],
  ["mol2", "mol2"],
  ["pdb", "pdb"],
  ["xyz", "xyz"],
  ["cif", "mmcif"],
  ["mmcif", "mmcif"],
] as const);

/**
 * Typed objects that carry viewable structure, with the formats each declares.
 *
 * The value side is what makes this a TYPE binding rather than a filename
 * sniff: a `.pdb` payload hanging off a `ConformerEnsemble` is not selected,
 * because `ConformerGeometry.format` cannot be `pdb`. A payload whose
 * extension the owning type does not declare is a producer bug, and rendering
 * it anyway would hide that.
 *
 * A `Map`, not an object literal, for both this and the extension table: the
 * keys are compared against a `typed_object` string and a filename extension
 * that both arrive from the server, and a bare property lookup on an object
 * resolves `"constructor"` or `"toString"` through `Object.prototype` to a
 * function that is very much not `undefined`. A `Map` has no prototype chain
 * to walk into, so an unknown name is simply absent. Pinned by
 * `molecule-structure.test.ts`.
 */
export const STRUCTURE_BEARING_TYPES: ReadonlyMap<string, readonly StructureFormat[]> = new Map([
  ["Molecule", ["sdf", "mol2", "pdb"]],
  ["ProteinStructure", ["pdb", "mmcif"]],
  ["ConformerEnsemble", ["sdf", "xyz"]],
] as const);

/** Whether `typedObject` is a type this viewer knows how to render. */
export function isStructureBearingType(typedObject: string): boolean {
  return STRUCTURE_BEARING_TYPES.has(typedObject);
}

/** The structure format implied by a payload's file extension, if any. */
export function formatForPayloadName(payloadName: string): StructureFormat | null {
  const dot = payloadName.lastIndexOf(".");
  if (dot <= 0 || dot === payloadName.length - 1) return null;
  const extension = payloadName.slice(dot + 1).toLowerCase();
  return EXTENSION_TO_FORMAT.get(extension) ?? null;
}

/** A payload selected for rendering, with the format to parse it as. */
export interface ViewablePayload<P> {
  payload: P;
  format: StructureFormat;
}

/**
 * Pick the payload to render for `typedObject`, or `null` if there is none.
 *
 * Selection is deliberately ordered by the TYPE's declared format list rather
 * than by payload order: a `Molecule` that carries both a `.pdb` and a `.sdf`
 * renders from the SDF, because `Molecule`'s list leads with `sdf` — the
 * inter-tile standard that keeps bond orders, which a PDB drops. Ordering by
 * whichever payload happened to be registered first would make the depiction
 * depend on producer bookkeeping.
 *
 * Returns `null` — never a guess — when the type is unknown, when no payload
 * has a recognised extension, or when the only recognised payloads are in
 * formats this type does not declare. An honest "nothing to show here" beats a
 * viewer rendering something it cannot vouch for (contract C3).
 */
export function pickViewablePayload<P extends { payload_name: string }>(
  typedObject: string,
  payloads: readonly P[]
): ViewablePayload<P> | null {
  const declared = STRUCTURE_BEARING_TYPES.get(typedObject);
  if (declared === undefined) return null;

  for (const format of declared) {
    const match = payloads.find((p) => formatForPayloadName(p.payload_name) === format);
    if (match !== undefined) return { payload: match, format };
  }
  return null;
}
